/**
 * ToolRunner — LC 학습 흐름에서 interactive (kind=tool → QueBoard) 컴포넌트를 렌더
 *
 * - shared-learning 의 QueBoardSession 을 `variant='inline'` 으로 렌더
 * - 세션 내 모든 수식 완료 시 completeSession 호출 후 onComplete() 콜백으로
 *   ComponentRunner 에 진행 신호 전달
 */
import { useCallback, useState } from 'react'
import { QueBoardSession } from '@quebon/shared-learning'
import { completeSession, type LcToolData } from '@/api/lcLearning'
import { Card } from './ui/Card'
import type { RuntimeMode } from './ComponentRunner'

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  toolData: LcToolData
  runtimeMode?: RuntimeMode
  onComplete: () => void
}

export default function ToolRunner({
  componentProgressId,
  enrollmentId,
  componentType,
  toolData,
  runtimeMode = 'live',
  onComplete,
}: Props) {
  const isLive = runtimeMode === 'live'
  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSessionComplete = useCallback(async () => {
    if (completing) return
    setCompleting(true)
    setError(null)
    try {
      if (isLive && componentProgressId != null) {
        await completeSession(componentProgressId, {
          enrollmentId,
          componentType,
        })
      }
      onComplete()
    } catch (e) {
      console.error('Tool completeSession failed', e)
      setError('완료 처리 중 오류가 발생했습니다.')
      setCompleting(false)
    }
  }, [completing, isLive, componentProgressId, enrollmentId, componentType, onComplete])

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <QueBoardSession
        variant="inline"
        isOpen={true}
        configs={toolData.configs}
        onSessionComplete={handleSessionComplete}
      />

      {error && (
        <Card size="md" className="w-full flex items-center gap-3">
          <div
            aria-label="error"
            className="w-8 h-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0"
          >
            ✕
          </div>
          <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">
            {error}
          </p>
        </Card>
      )}
    </div>
  )
}
